import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { postsService } from './postsService';
import { Lead, LeadUpdateInput, PipelineStage, Note, Activity, Email, EmailQuestion } from '../types';

function firestoreDate(v: unknown): Date | undefined {
  if (v == null) return undefined;
  if (v instanceof Timestamp) return v.toDate();
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  return undefined;
}

function mapLeadEmails(raw: unknown): Email[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => item != null && typeof item === 'object')
    .map((item, index) => {
    const ts =
      firestoreDate(item.timestamp) ??
      firestoreDate(item.sentAt) ??
      firestoreDate(item.createdAt) ??
      new Date(0);
    const questionsRaw = item.questions;
    const questions: EmailQuestion[] = Array.isArray(questionsRaw)
      ? questionsRaw.map((q: Record<string, unknown>, qi: number) => ({
          id: String(q.id ?? `q-${index}-${qi}`),
          question: String(q.question ?? ''),
          answered: !!q.answered,
          answer: q.answer != null ? String(q.answer) : undefined,
        }))
      : [];
    const allAnswered = questions.length === 0 ? true : questions.every((q) => q.answered);
    return {
      id: String(item.id ?? `email-${index}`),
      subject: String(item.subject ?? ''),
      content: String(item.content ?? item.body ?? ''),
      direction: item.direction === 'received' ? 'received' : 'sent',
      timestamp: ts,
      isRead: !!item.isRead,
      hasResponse: !!item.hasResponse,
      questions,
      allQuestionsAnswered: allAnswered,
      needsFollowUp: !!item.needsFollowUp,
      followUpSuggestion:
        item.followUpSuggestion != null ? String(item.followUpSuggestion) : undefined,
      threadId: item.threadId != null ? String(item.threadId) : undefined,
    };
  });
}

function mapLeadDoc(docSnap: { id: string; data: () => Record<string, unknown> }): Lead {
  const d = docSnap.data();
  const emails = mapLeadEmails(d.emails);
  return {
    id: docSnap.id,
    ...d,
    createdAt: firestoreDate(d.createdAt) ?? new Date(),
    updatedAt: firestoreDate(d.updatedAt) ?? new Date(),
    nextFollowUp: firestoreDate(d.nextFollowUp),
    consentMarketingRecordedAt: firestoreDate(d.consentMarketingRecordedAt),
    consentPrivacyPolicyRecordedAt: firestoreDate(d.consentPrivacyPolicyRecordedAt),
    doubleOptInConfirmedAt: firestoreDate(d.doubleOptInConfirmedAt),
    emails,
  } as Lead;
}

export const leadsService = {
  // Get all leads for organization (visible to all users in the org)
  async getLeads(userId: string, orgId: string): Promise<Lead[]> {
    try {
      // Query all leads in the organization (not just assigned to current user)
      // This allows team members to see and collaborate on all leads
      const q = query(
        collection(db, 'leads'),
        where('orgId', '==', orgId),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const leads = querySnapshot.docs.map((doc) => mapLeadDoc(doc));
      
      console.log(`✅ Loaded ${leads.length} leads for organization ${orgId}`);
      return leads;
    } catch (error) {
      console.error('Error getting leads:', error);
      throw error;
    }
  },

  // Listen to leads changes in real-time for entire organization
  onLeadsChange(userId: string, orgId: string, callback: (leads: Lead[]) => void, onError?: (error: any) => void): () => void {
    // Query all leads in the organization for real-time updates
    const q = query(
      collection(db, 'leads'),
      where('orgId', '==', orgId),
      orderBy('updatedAt', 'desc')
    )
    
    return onSnapshot(q, 
      (querySnapshot) => {
        try {
          const leads = querySnapshot.docs.map((doc) => mapLeadDoc(doc));
          
          console.log('Successfully loaded', leads.length, 'leads from Firestore');
          callback(leads);
        } catch (error) {
          console.error('Error processing leads data:', error);
          onError?.(error);
        }
      },
      (error) => {
        console.error('Firestore leads subscription error:', error);
        onError?.(error);
      }
    );
  },

  // Create new lead
  async createLead(leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt' | 'notes' | 'activities' | 'emails'> & { orgId?: string; createdBy?: string; createdByName?: string }): Promise<string> {
    try {
      const now = Timestamp.now();
      const leadWithTimestamps = {
        ...leadData,
        createdAt: now,
        updatedAt: now,
        nextFollowUp: leadData.nextFollowUp ? Timestamp.fromDate(leadData.nextFollowUp) : null,
        orgId: (leadData as any).orgId,
        createdBy: (leadData as any).createdBy,
        productId: (leadData as any).productId || null,
        product: (leadData as any).product || null,
        notes: [],
        activities: [{
          id: Date.now().toString(),
          type: 'stage_changed',
          description: 'Lead created',
          createdAt: now.toDate()
        }],
        emails: []
      };
      
      // Remove undefined values - Firestore doesn't accept them
      const cleanedLead = Object.fromEntries(
        Object.entries(leadWithTimestamps).filter(([_, value]) => value !== undefined)
      );
      
      const docRef = await addDoc(collection(db, 'leads'), cleanedLead);
      // Feed system post
      try {
        if ((leadData as any).orgId) {
          await postsService.createSystemPost({
            orgId: (leadData as any).orgId!,
            type: 'lead_created',
            createdById: (leadData as any).createdBy || 'system',
            createdByName: (leadData as any).createdByName || 'System',
            entityType: 'lead',
            entityId: docRef.id,
            entityLabel: `${leadData.firstName} ${leadData.lastName}`
          });

          // Check for milestones - Echobot celebration
          const allLeads = await this.getLeads((leadData as any).createdBy || '', (leadData as any).orgId);
          const leadCount = allLeads.length;

          // First lead milestone
          if (leadCount === 1) {
            await postsService.createEchobotPost({
              orgId: (leadData as any).orgId!,
              type: 'echobot_milestone',
              content: 'Glückwunsch! Dein erster Lead! 🎉 Der Anfang deiner Erfolgsgeschichte!',
              animation: 'celebrate'
            });
          }

          // 10 leads milestone
          if (leadCount === 10) {
            await postsService.createEchobotPost({
              orgId: (leadData as any).orgId!,
              type: 'echobot_milestone',
              content: 'Wow! 10 Leads erreicht! Du bist auf dem richtigen Weg! 🌟',
              animation: 'celebrate',
              actionButton: {
                label: 'Mehr Tipps',
                action: 'navigate-to-leads'
              }
            });
          }

          // 50 leads milestone
          if (leadCount === 50) {
            await postsService.createEchobotPost({
              orgId: (leadData as any).orgId!,
              type: 'echobot_milestone',
              content: 'Unglaublich! 50 Leads! Du rockst! 🚀',
              animation: 'celebrate'
            });
          }
        }
      } catch (e) {
        console.error('Error creating feed posts:', e);
      }
      return docRef.id;
    } catch (error) {
      console.error('Error creating lead:', error);
      throw error;
    }
  },

  // Update lead
  async updateLead(leadId: string, updates: LeadUpdateInput): Promise<void> {
    try {
      const leadRef = doc(db, 'leads', leadId);
      const {
        nextFollowUp,
        consentMarketingRecordedAt,
        consentPrivacyPolicyRecordedAt,
        doubleOptInConfirmedAt,
        ...rest
      } = updates;

      const updateData: Record<string, unknown> = {
        ...rest,
        updatedAt: Timestamp.now(),
      };

      if ('nextFollowUp' in updates) {
        updateData.nextFollowUp =
          nextFollowUp != null ? Timestamp.fromDate(nextFollowUp instanceof Date ? nextFollowUp : new Date(nextFollowUp as unknown as string)) : null;
      }

      const consentAtKeys = [
        ['consentMarketingRecordedAt', consentMarketingRecordedAt],
        ['consentPrivacyPolicyRecordedAt', consentPrivacyPolicyRecordedAt],
        ['doubleOptInConfirmedAt', doubleOptInConfirmedAt],
      ] as const;
      for (const [key, val] of consentAtKeys) {
        if (key in updates) {
          updateData[key] =
            val != null
              ? Timestamp.fromDate(val instanceof Date ? val : new Date(val as unknown as string))
              : null;
        }
      }

      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await updateDoc(leadRef, updateData as Record<string, unknown>);
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  },

  // Update lead stage
  async updateLeadStage(leadId: string, newStage: PipelineStage, userId: string): Promise<void> {
    try {
      const leadRef = doc(db, 'leads', leadId);
      const activity: Activity = {
        id: Date.now().toString(),
        type: 'stage_changed',
        description: `Stage changed to ${newStage}`,
        createdAt: new Date()
      };
      
      // Get current lead to append to activities
      const leadDoc = await getDoc(leadRef);
      const currentLead = leadDoc.data();
      const currentActivities = currentLead?.activities || [];
      
      await updateDoc(leadRef, {
        stage: newStage,
        updatedAt: Timestamp.now(),
        activities: [...currentActivities, activity]
      });

      // system feed post
      try {
        if ((currentLead as any)?.orgId) {
          await postsService.createSystemPost({
            orgId: (currentLead as any).orgId,
            type: 'lead_stage_changed',
            createdById: userId || 'system',
            createdByName: 'System',
            entityType: 'lead',
            entityId: leadId,
            entityLabel: `${(currentLead as any).firstName || ''} ${(currentLead as any).lastName || ''}`.trim()
          });
        }
      } catch {}
    } catch (error) {
      console.error('Error updating lead stage:', error);
      throw error;
    }
  },

  // Add note to lead
  async addNoteToLead(leadId: string, note: Omit<Note, 'id'>): Promise<Note> {
    try {
      const leadRef = doc(db, 'leads', leadId);
      const leadDoc = await getDoc(leadRef);
      const currentLead = leadDoc.data();
      const currentNotes = currentLead?.notes || [];
      
      const newNote: Note = {
        ...note,
        id: Date.now().toString(),
        createdAt: new Date()
      };
      
      await updateDoc(leadRef, {
        notes: [...currentNotes, newNote],
        updatedAt: Timestamp.now()
      });
      return newNote;
    } catch (error) {
      console.error('Error adding note to lead:', error);
      throw error;
    }
  },

  // Delete lead
  async deleteLead(leadId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'leads', leadId));
    } catch (error) {
      console.error('Error deleting lead:', error);
      throw error;
    }
  },

  // Search leads across entire organization
  async searchLeads(userId: string, orgId: string, searchTerm: string): Promise<Lead[]> {
    try {
      // Search all leads in the organization (not just assigned to current user)
      const q = query(
        collection(db, 'leads'),
        where('orgId', '==', orgId)
      );
      
      const querySnapshot = await getDocs(q);
      const allLeads = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        nextFollowUp: doc.data().nextFollowUp?.toDate()
      })) as Lead[];
      
      // Filter on client side for text search
      const filtered = allLeads.filter(lead => 
        (lead.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      console.log(`🔍 Search found ${filtered.length} leads matching "${searchTerm}" in organization ${orgId}`);
      return filtered;
    } catch (error) {
      console.error('Error searching leads:', error);
      throw error;
    }
  }
};